import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

interface LeavesTogetherProps {
  /** Whether the block belongs on screen. */
  present: boolean;
  /** Seconds the departure takes; match whatever it is leaving alongside. */
  exitSeconds: number;
  /**
   * Also close the space the block occupies, over the same seconds. Needed
   * where it is a block of its own above the content rather than a row inside
   * the same card: fading alone holds its height to the last frame and then
   * drops it, so everything below jumps at the moment the fade ends.
   */
  collapse?: boolean;
  children: ReactNode;
}

/**
 * Holds a block on screen for the length of its neighbour's departure.
 *
 * A toolbar gated on the loading flag unmounts the instant the query settles,
 * while the content beside it is still fading — so the content jumps into the
 * space the toolbar just gave up, halfway through its own animation. Fading out
 * over the same duration keeps the space until there is nothing left to shift
 * underneath.
 */
export function LeavesTogether(props: LeavesTogetherProps) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) return props.present ? <>{props.children}</> : null;

  return (
    <AnimatePresence initial={false}>
      {props.present && (
        <motion.div
          // `auto` so the height is measured rather than assumed; it is only
          // read on the way out, where motion needs somewhere to animate from.
          initial={props.collapse ? { opacity: 1, height: 'auto' } : { opacity: 1 }}
          animate={props.collapse ? { opacity: 1, height: 'auto' } : { opacity: 1 }}
          exit={props.collapse ? { opacity: 0, height: 0 } : { opacity: 0 }}
          style={props.collapse ? { overflow: 'hidden' } : undefined}
          transition={{ duration: props.exitSeconds, ease: 'easeOut' }}
        >
          {props.children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
